import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { useCandidate, useCreateCandidate, useOpenings, useUpdateCandidate } from '../queries';
import type { CandidateInput } from '../types';

const CANDIDATES_PATH = '/hrm/recruitment/candidates';

const schema = z.object({
  first_name: z.string().trim().min(1, 'First name is required').max(100),
  last_name: z.string().max(100),
  email: z.string().email('Enter a valid email').or(z.literal('')),
  phone_number: z.string().max(32),
  telegram: z.string().max(64),
  whatsapp: z.string().max(64),
  source: z.string().max(100),
  job_opening: z.string(),
  notes: z.string(),
});

type FormValues = z.infer<typeof schema>;

export function NewCandidatePage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const create = useCreateCandidate();
  const update = useUpdateCandidate();
  const { data: existing } = useCandidate(editId ?? '');
  const { data: openingsData } = useOpenings();
  const openings = useMemo(() => openingsData?.results ?? [], [openingsData]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: '', last_name: '', email: '', phone_number: '', telegram: '', whatsapp: '',
      source: '', job_opening: '', notes: '',
    },
  });

  // Prefill once the candidate loads (edit mode).
  useEffect(() => {
    if (existing) {
      form.reset({
        first_name: existing.first_name,
        last_name: existing.last_name,
        email: existing.email,
        phone_number: existing.phone_number,
        telegram: existing.telegram,
        whatsapp: existing.whatsapp,
        source: existing.source,
        job_opening: existing.job_opening ? String(existing.job_opening) : '',
        notes: existing.notes,
      });
    }
  }, [existing, form]);

  const backTo = isEdit ? `${CANDIDATES_PATH}/${editId}` : CANDIDATES_PATH;

  async function handleSubmit(values: FormValues) {
    const input: CandidateInput = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      phone_number: values.phone_number,
      telegram: values.telegram,
      whatsapp: values.whatsapp,
      source: values.source,
      job_opening: values.job_opening ? Number(values.job_opening) : null,
      // Status is driven by the board (drag between stages) — keep the existing
      // value on edit; new candidates start in "new".
      status: isEdit && existing ? existing.status : 'new',
      notes: values.notes,
      owner: existing?.owner ?? null,
    };
    try {
      if (isEdit && editId) {
        await update.mutateAsync({ id: editId, input });
      } else {
        await create.mutateAsync(input);
      }
      navigate(backTo);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          form.setError(field as keyof FormValues, { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <div className="w-full max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(backTo)}>
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit candidate' : 'New candidate'}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormSection variant="card" title="Candidate">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone number</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 555 123 4567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="telegram"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telegram</FormLabel>
                    <FormControl>
                      <Input placeholder="@handle" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="whatsapp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>WhatsApp</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 555 123 4567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>
          <FormSection variant="card" title="Application">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="job_opening"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opening</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Unassigned" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {openings.map((o) => (
                          <SelectItem key={o.id} value={String(o.id)}>
                            {o.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source</FormLabel>
                    <FormControl>
                      <Input placeholder="LinkedIn, referral…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>
          <FormSection variant="card" title="Notes">
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <MarkdownEditor
                      value={field.value}
                      onChange={field.onChange}
                      rows={8}
                      placeholder="Background, screening notes, links…"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <div className="flex gap-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Create candidate'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(backTo)}>
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
